import React, { useRef, useState } from 'react';

/**
 * Reusable 6-Box OTP Input component with single underlying input.
 * Preserves 100% exact visual design of MangoLoops 6 OTP boxes
 * while enabling 100% flawless Mobile WebOTP API, iOS Safari autofill, Gboard clipboard, and System Paste.
 */
export const OtpInputBox = ({
  otpDigits = ['', '', '', '', '', ''],
  onChange,
  onComplete,
  disabled = false,
  boxClassName = 'otp-box',
  containerClassName = 'otp-inputs',
}) => {
  const hiddenInputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const rawValue = Array.isArray(otpDigits) ? otpDigits.join('') : String(otpDigits || '');

  const handleChange = (e) => {
    const val = e.target.value;
    const match = val.match(/\b\d{6}\b/);
    const cleaned = match ? match[0] : val.replace(/\D/g, '').slice(0, 6);

    const newDigits = Array(6).fill('');
    cleaned.split('').forEach((d, i) => {
      if (i < 6) newDigits[i] = d;
    });

    if (typeof onChange === 'function') {
      onChange(newDigits, cleaned);
    }

    if (cleaned.length === 6 && typeof onComplete === 'function') {
      onComplete(cleaned);
    }
  };

  const handleBoxClick = () => {
    if (disabled) return;
    if (hiddenInputRef.current) {
      hiddenInputRef.current.focus();
      const len = rawValue.length;
      try {
        hiddenInputRef.current.setSelectionRange(len, len);
      } catch (err) {}
    }
  };

  const activeIndex = Math.min(5, rawValue.length);

  return (
    <div
      className={containerClassName}
      style={{ position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer' }}
      onClick={handleBoxClick}
    >
      <style>{`
        @keyframes otpCaretBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .otp-caret-line {
          display: inline-block;
          width: 2px;
          height: 1.25em;
          background-color: #099cf1;
          animation: otpCaretBlink 1s infinite;
          border-radius: 1px;
        }
      `}</style>
      {/* 6 Visual Boxes matching exact MangoLoops CSS */}
      {Array(6)
        .fill('')
        .map((_, index) => {
          const digit = otpDigits[index] || '';
          const isActive = isFocused && index === activeIndex;
          return (
            <div
              key={index}
              className={`${boxClassName} ${isActive ? 'focused active' : ''}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
                borderColor: isActive ? 'rgb(9, 156, 241)' : undefined,
                boxShadow: isActive ? '0 0 0 2px rgba(9, 156, 241, 0.2)' : undefined,
              }}
            >
              {digit ? digit : (isActive ? <span className="otp-caret-line" /> : null)}
            </div>
          );
        })}

      {/* Single Transparent Input Overlay for 100% Mobile Auto-Fill & WebOTP compatibility */}
      <input
        ref={hiddenInputRef}
        type="tel"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={rawValue}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        aria-label="One-Time Password"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          zIndex: 2,
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: 'transparent',
        }}
      />
    </div>
  );
};

export default OtpInputBox;
